import classnames from 'classnames';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { ThemeColors } from '../../../theme';
import { StyledDivComponent } from '../../../types';
import './flex-grid.scss';
import './print-grid.scss';

export type BreakpointNames =
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge';

export type ShowForBreakpointNames = 'medium' | 'large';

export type ShowOnlyForBreakpointNames = 'small' | ShowForBreakpointNames;

export type HorizontalAlignments = 'center' | 'right' | 'justify' | 'spaced';

export type VerticalAlignments = 'top' | 'middle' | 'bottom' | 'stretch';

export type SpaceControls = 'auto' | 'grow' | 'shrink';

export type FlexVerticalAlign =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'stretch';

export type Float = 'left' | 'center' | 'right';

interface GeneralProps {
  showFor?: ShowForBreakpointNames;
  showOnlyFor?: ShowOnlyForBreakpointNames;
  hideFor?: ShowForBreakpointNames;
  hideOnlyFor?: ShowOnlyForBreakpointNames;
  isHidden?: boolean;
  isInvisible?: boolean;
  showForLandscape?: boolean;
  showForPortrait?: boolean;
  showForSr?: boolean;
  showOnFocus?: boolean;
  isClearfix?: boolean;
  float?: Float;
}

interface FlexboxProps {
  alignX?: HorizontalAlignments;
  alignY?: VerticalAlignments;
  selfAlignX?: HorizontalAlignments;
  selfAlignY?: VerticalAlignments;
  centerAlign?: boolean;
  flexContainer?: boolean;
  flexDirRow?: BreakpointNames | 'all';
  flexDirRowRev?: BreakpointNames | 'all';
  flexDirCol?: BreakpointNames | 'all';
  flexDirColRev?: BreakpointNames | 'all';
  flexChild?: SpaceControls;
  flexOrder?: number;
  flexOrderSmall?: number;
  flexOrderMedium?: number;
  flexOrderLarge?: number;
}

interface DivProps {
  className?: string;
  children?: ReactNode;
}

const getGeneralClassNames = ({
  showFor,
  showOnlyFor,
  hideFor,
  hideOnlyFor,
  isHidden,
  isInvisible,
  showForLandscape,
  showForPortrait,
  showForSr,
  showOnFocus,
  isClearfix,
  float,
  ...props
}: GeneralProps) => [
  classnames(
    typeof showFor !== 'undefined' ? `show-for-${showFor}` : null,
    typeof showOnlyFor !== 'undefined' ? `show-for-${showOnlyFor}-only` : null,
    typeof hideFor !== 'undefined' ? `hide-for-${hideFor}` : null,
    typeof hideOnlyFor !== 'undefined' ? `hide-for-${hideOnlyFor}-only` : null,
    typeof float !== 'undefined' ? `float-${float}` : null,
    {
      hide: isHidden,
      invisible: isInvisible,
      'show-for-landscape': showForLandscape,
      'show-for-portrait': showForPortrait,
      'show-for-sr': showForSr,
      'show-on-focus': showOnFocus,
      clearfix: isClearfix
    }
  ),
  props
];

const mapFlexAlignToVerticalAlign = (align: FlexVerticalAlign) => {
  switch (align) {
    case 'flex-start':
      return 'top';
    case 'flex-end':
      return 'bottom';
    case 'center':
      return 'middle';
    default:
      return 'stretch';
  }
};

interface ColumnProps extends GeneralProps, FlexboxProps, DivProps {
  small?: number;
  medium?: number;
  large?: number;
  offsetOnSmall?: number;
  offsetOnMedium?: number;
  offsetOnLarge?: number;
  pushOnSmall?: number;
  pushOnMedium?: number;
  pushOnLarge?: number;
  pullOnSmall?: number;
  pullOnMedium?: number;
  pullOnLarge?: number;
  orderOnSmall?: number;
  orderOnMedium?: number;
  orderOnLarge?: number;
  centerOnSmall?: boolean;
  centerOnMedium?: boolean;
  centerOnLarge?: boolean;
  uncenterOnSmall?: boolean;
  uncenterOnMedium?: boolean;
  uncenterOnLarge?: boolean;
  expandOnSmall?: boolean;
  expandOnMedium?: boolean;
  expandOnLarge?: boolean;
  isShrunk?: boolean;
  isLast?: boolean;
  isColumn?: boolean;
}

interface LegacyColumnProps {
  alignSelf?: FlexVerticalAlign;
  bg?: keyof ThemeColors;
  noGap?: boolean;
}

const Column = ({
  className,
  small,
  medium,
  large,
  offsetOnSmall,
  offsetOnMedium,
  offsetOnLarge,
  pushOnSmall,
  pushOnMedium,
  pushOnLarge,
  pullOnSmall,
  pullOnMedium,
  pullOnLarge,
  orderOnSmall,
  orderOnMedium,
  orderOnLarge,
  centerOnSmall,
  centerOnMedium,
  centerOnLarge,
  uncenterOnSmall,
  uncenterOnMedium,
  uncenterOnLarge,
  expandOnSmall,
  expandOnMedium,
  expandOnLarge,
  isShrunk,
  isLast,
  isColumn,
  selfAlignY,
  alignSelf,
  bg,
  noGap,
  ...props
}: ColumnProps & LegacyColumnProps) => {
  const [generalClassNames, restProps] = getGeneralClassNames(props);
  return (
    <div
      {...restProps}
      className={classnames(
        isColumn ? 'column' : 'columns',
        className,
        typeof small !== 'undefined' ? `small-${small}` : null,
        typeof medium !== 'undefined' ? `medium-${medium}` : null,
        typeof large !== 'undefined' ? `large-${large}` : null,
        typeof offsetOnSmall !== 'undefined'
          ? `small-offset-${offsetOnSmall}`
          : null,
        typeof offsetOnMedium !== 'undefined'
          ? `medium-offset-${offsetOnMedium}`
          : null,
        typeof offsetOnLarge !== 'undefined'
          ? `large-offset-${offsetOnLarge}`
          : null,
        typeof pushOnSmall !== 'undefined' ? `small-push-${pushOnSmall}` : null,
        typeof pushOnMedium !== 'undefined'
          ? `medium-push-${pushOnMedium}`
          : null,
        typeof pushOnLarge !== 'undefined' ? `large-push-${pushOnLarge}` : null,
        typeof pullOnSmall !== 'undefined' ? `small-pull-${pullOnSmall}` : null,
        typeof pullOnMedium !== 'undefined'
          ? `medium-pull-${pullOnMedium}`
          : null,
        typeof pullOnLarge !== 'undefined' ? `large-pull-${pullOnLarge}` : null,
        typeof orderOnSmall !== 'undefined'
          ? `small-order-${orderOnSmall}`
          : null,
        typeof orderOnMedium !== 'undefined'
          ? `medium-order-${orderOnMedium}`
          : null,
        typeof orderOnLarge !== 'undefined'
          ? `large-order-${orderOnLarge}`
          : null,
        typeof selfAlignY !== 'undefined' ? `align-self-${selfAlignY}` : null,
        {
          'small-centered': centerOnSmall,
          'medium-centered': centerOnMedium,
          'large-centered': centerOnLarge,
          'small-uncentered': uncenterOnSmall,
          'medium-uncentered': uncenterOnMedium,
          'large-uncentered': uncenterOnLarge,
          'small-expand': expandOnSmall,
          'medium-expand': expandOnMedium,
          'large-expand': expandOnLarge,
          shrink: isShrunk,
          end: isLast
        },
        generalClassNames
      )}
    />
  );
};

const StyledColumn: StyledDivComponent<
  ColumnProps & LegacyColumnProps
> = styled(Column).attrs({
  selfAlignY: ({ alignSelf, selfAlignY }: ColumnProps & LegacyColumnProps) =>
    alignSelf ? mapFlexAlignToVerticalAlign(alignSelf) : selfAlignY || 'stretch'
})`
  background-color: ${props =>
    props.bg ? props.theme.colors[props.bg] : 'transparent'};
`;

interface RowProps extends GeneralProps, FlexboxProps, DivProps {
  upOnSmall?: number;
  upOnMedium?: number;
  upOnLarge?: number;
  horizontalAlignment?: HorizontalAlignments;
  verticalAlignment?: VerticalAlignments;
  unstackOnSmall?: boolean;
  unstackOnMedium?: boolean;
  unstackOnLarge?: boolean;
  collapseOnSmall?: boolean;
  collapseOnMedium?: boolean;
  collapseOnLarge?: boolean;
  uncollapseOnSmall?: boolean;
  uncollapseOnMedium?: boolean;
  uncollapseOnLarge?: boolean;
  isCollapsed?: boolean;
  isExpanded?: boolean;
  isColumn?: boolean;
}

interface LegacyRowProps {
  alignItems?: any;
  justifyContent?: any;
  noGap?: any;
  borderTop?: any;
  bg?: keyof ThemeColors;
}

const Row = ({
  className,
  upOnSmall,
  upOnMedium,
  upOnLarge,
  horizontalAlignment,
  verticalAlignment,
  unstackOnSmall,
  unstackOnMedium,
  unstackOnLarge,
  collapseOnSmall,
  collapseOnMedium,
  collapseOnLarge,
  uncollapseOnSmall,
  uncollapseOnMedium,
  uncollapseOnLarge,
  isCollapsed,
  isColumn,
  isExpanded,
  alignItems,
  justifyContent,
  noGap,
  borderTop,
  ...props
}: RowProps & LegacyRowProps) => {
  const [generalClassNames, restProps] = getGeneralClassNames(props);
  return (
    <div
      {...restProps}
      className={classnames(
        'row',
        className,
        typeof upOnSmall !== 'undefined' ? `small-up-${upOnSmall}` : null,
        typeof upOnMedium !== 'undefined' ? `medium-up-${upOnMedium}` : null,
        typeof upOnLarge !== 'undefined' ? `large-up-${upOnLarge}` : null,
        typeof horizontalAlignment !== 'undefined'
          ? `align-${horizontalAlignment}`
          : null,
        typeof verticalAlignment !== 'undefined'
          ? `align-${verticalAlignment}`
          : null,
        {
          'small-unstack': unstackOnSmall,
          'medium-unstack': unstackOnMedium,
          'large-unstack': unstackOnLarge,
          'small-collapse': collapseOnSmall,
          'medium-collapse': collapseOnMedium,
          'large-collapse': collapseOnLarge,
          'small-uncollapse': uncollapseOnSmall,
          'medium-uncollapse': uncollapseOnMedium,
          'large-uncollapse': uncollapseOnLarge,
          collapse: isCollapsed,
          expanded: isExpanded,
          column: isColumn
        },
        generalClassNames
      )}
    />
  );
};

const StyledRow: StyledDivComponent<RowProps & LegacyRowProps> = styled(
  Row
).attrs({
  verticalAlignment: ({ verticalAlignment, alignItems }) =>
    alignItems ? alignItems : verticalAlignment || 'stretch',
  horizontalAlignment: ({ horizontalAlignment, justifyContent }) =>
    justifyContent ? justifyContent : horizontalAlignment || 'space-between',
  isCollapsed: ({ noGap, isCollapsed }) => noGap || isCollapsed
})`
  border-top: ${props =>
    props.borderTop ? '1px solid ' + props.theme.colors.grayLight : 'none'};
  background-color: ${props =>
    props.bg ? props.theme.colors[props.bg] : 'transparent'};
`;

export { StyledRow as Row, StyledColumn as Column };
